export interface PollUntilOptions {
  intervalMs?: number;
  timeoutMs?: number;
  /** Caller flips `cancelled` (e.g. on unmount) to stop polling early. */
  signal?: { cancelled: boolean };
}

export interface PollUntilResult {
  ok: boolean;
  reason?: "timeout" | "cancelled";
}

/**
 * Repeatedly runs `probe` until `predicate(result)` is true. The first probe
 * fires immediately; subsequent probes every `intervalMs`. Probe rejections
 * count as failed attempts (polling continues until timeout) — this never
 * throws.
 */
export async function pollUntil<T>(
  probe: () => Promise<T>,
  predicate: (result: T) => boolean,
  { intervalMs = 1000, timeoutMs = 10000, signal }: PollUntilOptions = {},
): Promise<PollUntilResult> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    if (signal?.cancelled) return { ok: false, reason: "cancelled" };
    try {
      if (predicate(await probe())) return { ok: true };
    } catch {
      // Failed attempt — keep polling until the deadline.
    }
    if (signal?.cancelled) return { ok: false, reason: "cancelled" };
    if (Date.now() + intervalMs > deadline) return { ok: false, reason: "timeout" };
    await new Promise(r => setTimeout(r, intervalMs));
  }
}
